import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface SupplyProduct {
  id: string;
  name: string;
  description?: string;
  price: number;
  sellingPrice: number;
  returnRate: number;
  raised: number;
  target: number;
  stockQuantity: number;
  type: "trial" | "vip";
  tag: string;
  status?: string;
  liked?: boolean;
  category?: string;
  imageUrl?: string;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const DEFAULT_LIMIT = 1000;
const PAGE_SIZE = 50;

function getCacheKey(type?: "trial" | "vip", paginated?: boolean): string {
  const base = type ? `shop_products_${type}_cache_v2` : `shop_products_all_cache_v2`;
  if (paginated) return `${base}_paginated`;
  return base;
}

// 清理舊版 v1 快取，避免 paginated 共用 key 造成的髒資料殘留
(function clearV1Caches() {
  const v1Keys = [
    "shop_products_paginated_cache_v1",
    "shop_products_trial_cache_v1",
    "shop_products_vip_cache_v1",
    "shop_products_all_cache_v1",
  ];
  v1Keys.forEach((k) => {
    try { sessionStorage.removeItem(k); } catch { /* ignore */ }
  });
})();

function getCached(
  type?: "trial" | "vip",
  paginated?: boolean,
): { data: SupplyProduct[]; timestamp: number } | null {
  try {
    const raw = sessionStorage.getItem(getCacheKey(type, paginated));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.data || !parsed.timestamp) return null;
    if (Date.now() - parsed.timestamp > CACHE_TTL) {
      sessionStorage.removeItem(getCacheKey(type, paginated));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function setCached(data: SupplyProduct[], type?: "trial" | "vip", paginated?: boolean) {
  try {
    sessionStorage.setItem(
      getCacheKey(type, paginated),
      JSON.stringify({ data, timestamp: Date.now() }),
    );
  } catch {
    // ignore
  }
}

function formatProduct(item: Record<string, unknown>): SupplyProduct {
  const price = item.price as number;
  const sellingPrice = (item.selling_price as number) ?? Math.round(price * (1 + Number((item.return_rate as number) || 25) / 100));
  const calculatedReturnRate = price > 0 ? ((sellingPrice - price) / price) * 100 : 0;
  return {
    id: item.id as string,
    name: item.name as string,
    description: (item.description as string) || "",
    price,
    sellingPrice,
    returnRate: calculatedReturnRate,
    raised: item.raised as number,
    target: item.target as number,
    stockQuantity: (item.stock_quantity as number) || 0,
    type: item.type as "trial" | "vip",
    tag:
      (item.tag as string) ||
      ((item.type as string) === "trial" ? "體驗" : "VIP"),
    status: item.status as string,
    category: (item.category as string) || "未分類",
    imageUrl: (item.image_url as string) || undefined,
  };
}

interface UseProductsOptions {
  paginated?: boolean;
}

export function useProducts(type?: "trial" | "vip", options?: UseProductsOptions) {
  const paginated = options?.paginated ?? false;
  const [products, setProducts] = useState<SupplyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const fetchedKeyRef = useRef("");
  const offsetRef = useRef(0);

  // ---- Non-paginated: original full-load behavior ----
  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");

    let query = supabase
      .from("products")
      .select("id, name, description, price, selling_price, return_rate, raised, target, stock_quantity, type, tag, status, category, image_url, created_at")
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .limit(DEFAULT_LIMIT);

    if (type) {
      query = query.eq("type", type);
    }

    const { data, error: fetchError } = await query;

    if (fetchError) {
      setError(`讀取商品失敗：${fetchError.message}`);
    } else {
      const formatted = (data || []).map(formatProduct);
      setProducts(formatted);
      setCached(formatted, type);
    }
    setLoading(false);
  }, [type]);

  // ---- Paginated: load one page at a time ----
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);

    const start = offsetRef.current;
    const end = start + PAGE_SIZE - 1;

    let query = supabase
      .from("products")
      .select("id, name, description, price, selling_price, return_rate, raised, target, stock_quantity, type, tag, status, category, image_url, created_at")
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .range(start, end);

    if (type) {
      query = query.eq("type", type);
    }

    const { data, error: fetchError } = await query;

    if (fetchError) {
      setError(`讀取商品失敗：${fetchError.message}`);
      setLoadingMore(false);
      return;
    }

    const formatted = (data || []).map(formatProduct);
    if (formatted.length < PAGE_SIZE) {
      setHasMore(false);
    }

    setProducts((prev) => {
      const newProducts = [...prev, ...formatted];
      setCached(newProducts, type, paginated);
      return newProducts;
    });
    offsetRef.current = end + 1;
    setLoadingMore(false);
  }, [loadingMore, hasMore, type, paginated]);

  // ---- Paginated initial load ----
  const paginatedInitLoad = useCallback(async () => {
    setLoading(true);
    setError("");
    offsetRef.current = 0;
    setHasMore(true);

    let query = supabase
      .from("products")
      .select("id, name, description, price, selling_price, return_rate, raised, target, stock_quantity, type, tag, status, category, image_url, created_at")
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .range(0, PAGE_SIZE - 1);

    if (type) {
      query = query.eq("type", type);
    }

    const { data, error: fetchError } = await query;

    if (fetchError) {
      setError(`讀取商品失敗：${fetchError.message}`);
    } else {
      const formatted = (data || []).map(formatProduct);
      setProducts(formatted);
      setCached(formatted, type, paginated);
      if (formatted.length < PAGE_SIZE) {
        setHasMore(false);
      }
      offsetRef.current = PAGE_SIZE;
    }
    setLoading(false);
  }, [type, paginated]);

  useEffect(() => {
    const key = `${type ?? 'all'}_${paginated}`;
    if (fetchedKeyRef.current === key) return;
    fetchedKeyRef.current = key;

    // 切換類型時清空舊資料並重置分頁
    if (paginated) {
      offsetRef.current = 0;
      setHasMore(true);
    }
    setProducts([]);
    setLoading(true);
    setError("");

    if (paginated) {
      const cached = getCached(type, paginated);
      if (cached) {
        setProducts(cached.data);
        setHasMore(cached.data.length >= PAGE_SIZE);
        offsetRef.current = cached.data.length;
        setLoading(false);
        // Background silent refresh
        supabase
          .from("products")
          .select("id, name, description, price, selling_price, return_rate, raised, target, stock_quantity, type, tag, status, category, image_url, created_at")
          .eq("status", "active")
          .order("created_at", { ascending: false })
          .range(0, PAGE_SIZE - 1)
          .then(({ data, error: fetchError }) => {
            if (fetchError) return;
            const formatted = (data || []).map(formatProduct);
            setProducts(formatted);
            setCached(formatted, type, paginated);
            if (formatted.length < PAGE_SIZE) setHasMore(false);
            offsetRef.current = PAGE_SIZE;
          });
        return;
      }
      paginatedInitLoad();
    } else {
      // ---- Original non-paginated behavior ----
      const cached = getCached(type);
      if (cached) {
        setProducts(cached.data);
        setLoading(false);
        let query = supabase
          .from("products")
          .select("id, name, description, price, selling_price, return_rate, raised, target, stock_quantity, type, tag, status, category, image_url, created_at")
          .eq("status", "active")
          .order("created_at", { ascending: false })
          .limit(DEFAULT_LIMIT);

        if (type) {
          query = query.eq("type", type);
        }

        query.then(({ data, error: fetchError }) => {
          if (fetchError) {
            console.warn("[useProducts] background refresh failed:", fetchError);
            return;
          }
          const formatted = (data || []).map(formatProduct);
          setProducts(formatted);
          setCached(formatted, type);
        });
      } else {
        refresh();
      }
    }
  }, [refresh, paginatedInitLoad, type, paginated]);

  return { products, loading, loadingMore, error, hasMore, loadMore: paginated ? loadMore : undefined, refresh };
}